export function toNumber(value) {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : 0;
}

export function centimetersFromMetric(meters, centimeters) {
  return toNumber(meters) * 100 + toNumber(centimeters);
}

export function centimetersFromMetersAndInches(meters, inches) {
  return toNumber(meters) * 100 + toNumber(inches) * 2.54;
}

export function centimetersFromImperial(feet, inches) {
  return toNumber(feet) * 30.48 + toNumber(inches) * 2.54;
}

function unitToCentimeters(unit) {
  const normalized = String(unit || '').toLowerCase();

  if (['m', 'meter', 'meters', 'metre', 'metres'].includes(normalized)) {
    return 100;
  }

  if (['cm', 'centimeter', 'centimeters', 'centimetre', 'centimetres'].includes(normalized)) {
    return 1;
  }

  if (['ft', 'foot', 'feet'].includes(normalized)) {
    return 30.48;
  }

  if (['in', 'inch', 'inches', '"'].includes(normalized)) {
    return 2.54;
  }

  return 0;
}

export function parseRollDimensionsFromSize(sizeText) {
  const rawText = String(sizeText || '');

  if (/\bsample\b|a4/i.test(rawText)) {
    return { rollWidthCm: 0, rollLengthCm: 0, error: 'sample_size' };
  }

  const text = rawText
    .replace(/[×✕]/g, 'x')
    .replace(/[″”]/g, ' in ')
    .replace(/[“]/g, ' in ')
    .replace(/′/g, ' ft ');
  const dimensionPattern =
    /(\d+(?:\.\d+)?)\s*(cm|centimeters?|centimetres?|m|meters?|metres?|in|inch(?:es)?|"|ft|foot|feet)\s*x\s*(\d+(?:\.\d+)?)\s*(cm|centimeters?|centimetres?|m|meters?|metres?|in|inch(?:es)?|"|ft|foot|feet)/i;
  const match = text.match(dimensionPattern);

  if (!match) {
    return { rollWidthCm: 0, rollLengthCm: 0, error: 'unrecognized_size' };
  }

  const rollWidthCm = toNumber(match[1]) * unitToCentimeters(match[2]);
  const rollLengthCm = toNumber(match[3]) * unitToCentimeters(match[4]);

  return { rollWidthCm, rollLengthCm };
}

function cleanOptionText(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function isPlausibleSingleOptionText(text) {
  const cleaned = cleanOptionText(text);
  return cleaned.length > 0 && cleaned.length <= 80;
}

export function chooseRollSizeText(initialSizeText, candidateTexts = []) {
  const sampleCandidate = candidateTexts.find((text) => {
    const cleaned = cleanOptionText(text);
    return isPlausibleSingleOptionText(cleaned) && parseRollDimensionsFromSize(cleaned).error === 'sample_size';
  });
  const dimensionCandidate = candidateTexts.find((text) => {
    const cleaned = cleanOptionText(text);
    const parsed = parseRollDimensionsFromSize(cleaned);
    return isPlausibleSingleOptionText(cleaned) && parsed.rollWidthCm > 0 && parsed.rollLengthCm > 0;
  });

  return cleanOptionText(dimensionCandidate || sampleCandidate || initialSizeText);
}

export function calculateWallpaperRolls({
  wallWidthCm,
  wallHeightCm,
  rollWidthCm,
  rollLengthCm,
  patternRepeatCm = 0,
}) {
  const width = toNumber(wallWidthCm);
  const height = toNumber(wallHeightCm);
  const rollWidth = toNumber(rollWidthCm);
  const rollLength = toNumber(rollLengthCm);
  const repeat = Math.max(0, toNumber(patternRepeatCm));

  if (rollWidth <= 0 || rollLength <= 0) {
    return { error: 'missing_roll_dimensions' };
  }

  if (width <= 0 || height <= 0) {
    return { error: 'missing_wall_dimensions' };
  }

  const strips = Math.ceil(width / rollWidth);
  const adjustedDropCm = repeat > 0 ? Math.ceil(height / repeat) * repeat : height;
  const dropsPerRoll = Math.floor(rollLength / adjustedDropCm);

  if (dropsPerRoll < 1) {
    return { error: 'wall_too_tall', strips, adjustedDropCm, dropsPerRoll };
  }

  return {
    rolls: Math.ceil(strips / dropsPerRoll),
    strips,
    adjustedDropCm,
    dropsPerRoll,
  };
}

export function normalizeQuantityForInput(quantity, input) {
  const min = Math.max(1, toNumber(input?.min) || 1);
  const max = toNumber(input?.max);
  const step = Math.max(1, toNumber(input?.step) || 1);
  let normalized = Math.max(min, Math.ceil(toNumber(quantity)));

  if (step > 1) {
    normalized = min + Math.ceil((normalized - min) / step) * step;
  }

  if (max > 0) {
    normalized = Math.min(normalized, max);
  }

  return normalized;
}

function formatRollLabel(rolls) {
  return `${rolls} ${rolls === 1 ? 'roll' : 'rolls'}`;
}

function findQuantityInput(formId) {
  const form = document.getElementById(formId);
  return (
    form?.querySelector('[name="quantity"]') ||
    document.querySelector(`[name="quantity"][form="${formId}"]`)
  );
}

function selectedTextFromElement(element) {
  if (!element) {
    return '';
  }

  if (element.tagName === 'OPTION') {
    return element.textContent;
  }

  if (element.matches?.('input[type="radio"], input[type="checkbox"]')) {
    const escapedId =
      element.id && typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(element.id) : element.id;
    const label =
      element.closest('label') ||
      (escapedId ? document.querySelector(`label[for="${escapedId}"]`) : null);
    return label?.textContent || element.getAttribute('aria-label') || element.value;
  }

  return element.getAttribute?.('aria-label') || element.textContent;
}

function getSelectedRollSizeText(calculator) {
  const productInfo = calculator.closest('.product-info') || document;
  const selectedSelectors = [
    'input[type="radio"]:checked',
    'input[type="checkbox"]:checked',
    'select option:checked',
    '[aria-current="true"]',
    '[aria-checked="true"]',
    '[aria-selected="true"]',
    '[data-selected="true"]',
    '.is-selected',
    '.selected',
  ];
  const candidateTexts = selectedSelectors
    .flatMap((selector) => Array.from(productInfo.querySelectorAll(selector)))
    .map(selectedTextFromElement)
    .map(cleanOptionText)
    .filter(Boolean);

  return chooseRollSizeText(calculator.dataset.rollSize || '', candidateTexts);
}

function getWallDimensions(calculator, unit) {
  if (unit === 'imperial') {
    return {
      wallWidthCm: centimetersFromImperial(
        calculator.querySelector('[data-calculator-field="width-ft"]')?.value,
        calculator.querySelector('[data-calculator-field="width-in"]')?.value,
      ),
      wallHeightCm: centimetersFromImperial(
        calculator.querySelector('[data-calculator-field="height-ft"]')?.value,
        calculator.querySelector('[data-calculator-field="height-in"]')?.value,
      ),
    };
  }

  return {
    wallWidthCm: centimetersFromMetersAndInches(
      calculator.querySelector('[data-calculator-field="width-m"]')?.value,
      calculator.querySelector('[data-calculator-field="width-in"]')?.value,
    ),
    wallHeightCm: centimetersFromMetersAndInches(
      calculator.querySelector('[data-calculator-field="height-m"]')?.value,
      calculator.querySelector('[data-calculator-field="height-in"]')?.value,
    ),
  };
}

function showPanel(calculator, unit) {
  calculator.querySelectorAll('[data-unit-panel]').forEach((panel) => {
    panel.hidden = panel.dataset.unitPanel !== unit;
  });
}

function updateResult(calculator, result, quantityInput) {
  const resultNode = calculator.querySelector('[data-calculator-result]');
  const applyButton = calculator.querySelector('[data-calculator-apply]');

  if (!resultNode || !applyButton) {
    return;
  }

  if (result.error === 'missing_roll_dimensions') {
    if (calculator.dataset.rollSizeError === 'sample_size') {
      resultNode.textContent = 'Please select a roll size before calculating.';
    } else {
      resultNode.textContent = 'Roll size could not be read from this product size.';
    }

    applyButton.hidden = true;
    return;
  }

  if (result.error === 'missing_wall_dimensions') {
    resultNode.textContent = 'Enter the wall width and height to calculate rolls.';
    applyButton.hidden = true;
    return;
  }

  if (result.error === 'wall_too_tall') {
    resultNode.textContent = 'The wall height is taller than the configured roll length.';
    applyButton.hidden = true;
    return;
  }

  const appliedQuantity = normalizeQuantityForInput(result.rolls, quantityInput);
  resultNode.textContent = `You need ${formatRollLabel(result.rolls)}.`;
  applyButton.textContent = `Use ${formatRollLabel(appliedQuantity)}`;
  applyButton.dataset.quantity = String(appliedQuantity);
  applyButton.hidden = false;
}

function initializeCalculator(calculator) {
  if (calculator.dataset.initialized === 'true') {
    return;
  }

  calculator.dataset.initialized = 'true';

  const formId = calculator.dataset.productFormId || '';
  const quantityInput = findQuantityInput(formId);
  const unitToggle = calculator.querySelector('[data-calculator-unit-toggle]');
  const calculateButton = calculator.querySelector('[data-calculator-submit]');
  const applyButton = calculator.querySelector('[data-calculator-apply]');

  showPanel(calculator, unitToggle?.checked ? 'imperial' : 'metric');

  unitToggle?.addEventListener('change', () => {
    showPanel(calculator, unitToggle.checked ? 'imperial' : 'metric');
  });

  calculateButton?.addEventListener('click', () => {
    const unit = unitToggle?.checked ? 'imperial' : 'metric';
    const dimensions = getWallDimensions(calculator, unit);
    const currentRollSize = getSelectedRollSizeText(calculator);
    const sizeDimensions = parseRollDimensionsFromSize(currentRollSize);
    calculator.dataset.rollSizeError = sizeDimensions.error || '';
    const result = calculateWallpaperRolls({
      ...dimensions,
      rollWidthCm: sizeDimensions.rollWidthCm || calculator.dataset.rollWidthCm,
      rollLengthCm: sizeDimensions.rollLengthCm || calculator.dataset.rollLengthCm,
      patternRepeatCm: calculator.dataset.patternRepeatCm,
    });

    updateResult(calculator, result, quantityInput);
  });

  applyButton?.addEventListener('click', () => {
    if (!quantityInput || !applyButton.dataset.quantity) {
      return;
    }

    quantityInput.value = applyButton.dataset.quantity;
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

if (typeof window !== 'undefined') {
  class WallpaperRollCalculatorElement extends HTMLElement {
    connectedCallback() {
      initializeCalculator(this);
    }
  }

  if (!customElements.get('wallpaper-roll-calculator')) {
    customElements.define('wallpaper-roll-calculator', WallpaperRollCalculatorElement);
  }
}
